import { CjContainer } from '../layout-editor/model/cj-factory';
import { CjTool } from './cj-tool';
import { TrimCjTool } from './trim.cj-tool';
import { TrimCjContainer } from '../trim-editor/model/cj-factory';
import { Pos } from '../models/pos';

type posType = {
  x: number,
  y: number,
  w: number,
  h: number,
}

/**
 * 写真フィットチェック機能
 * 実行時は画像のコンテナを渡す
 * デバッグしたいときはStageも一緒に渡す
 */
export class FitChecker {
  /**
   * -- 写真FIT機能 --
   * @param {CjContainer} target
   * @param {CjContainer[]} photoFrames
   * @param {CjContainer} stage
   * @returns {boolean} フィットしていない時にtrueを返す
   */
  static checkImageOutBounds(target: CjContainer, photoFrames: CjContainer[], stage?: CjContainer) {
    // --- fitしなかった写真の配列 ---
    const nonFitImages: CjContainer[] = [];
    // --- 写真枠を全てチェック ---
    for (const photoFrame of photoFrames) {
      // ---- 写真がなければスキップ ----
      if (!CjTool.checkPhotoInFrame(photoFrame)) continue;
      if (CjTool.getImageAreaContainer(photoFrame)?.logoFlag) continue;
      // ---- corner: 枠の角 checker: 写真の範囲 photo: 写真のコンテナ ----
      const corners = CjTool.getDummyCorners(photoFrame);
      const checker = CjTool.getChecker(photoFrame);
      const photo = CjTool.getPhotoContainer(photoFrame);
      // ---- 判定用にフラグを立てる ----
      photo.isFit = true;
      // ---- 枠の角と写真で当たり判定による内包チェック ----
      for (const corner of corners) {
        const pos = corner.graphics.command as posType;
        const _point = corner.localToLocal(pos.x, pos.y, checker);
        const point = { ..._point, x: Math.round(_point.x), y: Math.round(_point.y) };
        const isHit = checker.hitTest(point.x, point.y);
        // ----- 内包していない場合、即時終了 -----
        if (!isHit) {
          photo.isFit = false;
          break;
        }
      }
      // ---- checkerのON・OFF ----
      if (photo.isFit) {
        checker.alpha = 0;
      } else {
        checker.alpha = 0.2;
        // ----- Fitしなかった写真をプッシュ -----
        nonFitImages.push(photo);
      }
    }
    // --- トースト用のフラグを返す ---
    return Boolean(nonFitImages.length);
  }

  /**
   * 画像が一枚のまとめてプリント用
   * @param {TrimCjContainer} target
   * @param {boolean} noColor 余白制御時に色をつけない用
   * @returns {boolean} フィットしていない時にtrueを返す
   */
  static checkImageOneOutBounds(target: TrimCjContainer, noColor?: boolean): boolean {

    // ---- corner: 枠の角 checker: 写真の範囲 photo: 写真のコンテナ ----
    const corners = TrimCjTool.getDummyCorners(target);
    const checker = TrimCjTool.getChecker(target);
    const photo = TrimCjTool.getPhotoContainer(target);
    // ---- 判定用にフラグを立てる ----
    photo.isFit = true;
    // ---- 枠の角と写真で当たり判定による内包チェック ----
    for (const corner of corners) {
      const pos = corner.graphics.command as posType;
      const _point = corner.localToLocal(pos.x, pos.y, checker);
      const point = { ..._point, x: Math.round(_point.x), y: Math.round(_point.y) };
      const isHit = checker.hitTest(point.x, point.y);
      // ----- 内包していない場合、即時終了 -----
      if (!isHit) {
        photo.isFit = false;
        break;
      }
    }
    // ---- checkerのON・OFF ----
    if (photo.isFit || noColor) {
      checker.alpha = 0;
    } else {
      checker.alpha = 0.2;
    }

    // --- トースト用のフラグを返す ---
    return !photo.isFit;
  }

  // - 余白チェック用OKになるギリギリを算出する -
  // -- 移動 --
  static findClosestValidPosition(container: TrimCjContainer, minPos: Pos, maxPos: Pos): Pos {
    let lowX = minPos.x, highX = maxPos.x, bestX = minPos.x;
    let lowY = minPos.y, highY = maxPos.y, bestY = minPos.y;

    while ((highX - lowX > 1) || (highY - lowY > 1)) {
      let midX = (lowX + highX) / 2;
      let midY = (lowY + highY) / 2;

      let newPos = { x: midX, y: midY };
      container.x = newPos.x;
      container.y = newPos.y;

      const isOutOfBounds = FitChecker.checkImageOneOutBounds(container, true);

      if (isOutOfBounds) {
        let canMoveX = true;
        let canMoveY = true;

        // **X軸のチェック（縮小OK・拡張NG）**
        container.x = bestX;  // Xを固定してYだけ調べる
        if (FitChecker.checkImageOneOutBounds(container, true)) {
          highY = midY; // Y方向に拡張しない
          canMoveY = false;
        } else {
          bestY = midY;
          lowY = midY;
        }

        // **Y軸のチェック（縮小OK・拡張NG）**
        container.y = bestY;  // Yを固定してXだけ調べる
        container.x = midX;
        if (FitChecker.checkImageOneOutBounds(container, true)) {
          highX = midX; // X方向に拡張しない
          canMoveX = false;
        } else {
          bestX = midX;
          lowX = midX;
        }

        // **どちらかの軸がまだ動けるなら続行**
        if (!canMoveX && !canMoveY) break;

      } else {
        bestX = midX;
        bestY = midY;
        lowX = midX;
        lowY = midY;
      }
    }
    return { x: bestX, y: bestY };
  }
  // -- 拡縮 --
  static findClosestValidScale(container: TrimCjContainer, minScale: number, maxScale: number): number {
    let low = minScale;
    let high = maxScale;
    let bestScale = minScale;

    while (high - low > 0.1) {
      const mid = (low + high) / 2;
      container.scaleX = mid;
      container.scaleY = mid;

      if (FitChecker.checkImageOneOutBounds(container, true)) {
        high = mid;
      } else {
        bestScale = mid;
        low = mid;
      }
    }

    return bestScale;
  }
  // -- 回転 --
  static findClosestValidRotation(container: TrimCjContainer, minAngle: number, maxAngle: number): number {
    const normalize = (angle: number) => (angle + 360) % 360;
    let low = normalize(minAngle);
    let high = normalize(maxAngle);
    let bestAngle = low;

    if (high === low) return low;

    // 時計回り：low < high（例：10 → 60）
    if ((high > low && high - low < 180) || (high < low && low - high > 180)) {
      while (high - low > 0.1) {
        const mid = (low + high) / 2;
        container.rotation = normalize(mid);
        if (FitChecker.checkImageOneOutBounds(container, true)) {
          high = mid;
        } else {
          bestAngle = normalize(mid);
          low = mid;
        }
      }
      return bestAngle;
    }

    // 反時計回り：low > high（例：350 → 300）
    // ⇒ 時計回りに見えて180度超えてるなら実は反時計回り
    let current = low;
    while (normalize(current - high) > 0.1) {
      container.rotation = normalize(current);
      if (!FitChecker.checkImageOneOutBounds(container, true)) {
        bestAngle = normalize(current);
      } else {
        break;
      }
      current -= 0.5;
    }

    return bestAngle;
  }
}
